import React, { useEffect, useState } from "react"
import {
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  Table,
  TabPane,
} from "reactstrap"
import moment from "moment"
import { get } from "helpers/api_helper"
import { formatDate, formatINR, formatMobile } from "helpers/common_helper"

const errorMessage = (err, fallback) =>
  err?.response?.data?.msg || err?.response?.data?.error || fallback

const ORDER_STATUS_BADGE = {
  pending: "badge-soft-warning",
  dispatched: "badge-soft-info",
  delivered: "badge-soft-success",
  returned: "badge-soft-primary",
  cancelled: "badge-soft-danger",
  refunded: "badge-soft-secondary",
}

const PAYMENT_MODE_LABEL = {
  cash: "Cash",
  upi: "UPI",
  bank_transfer: "Bank Transfer",
  card: "Card",
  complimentary: "Complimentary",
  other: "Other",
}

const rangeText = range => {
  if (!range) return "-"
  const from = Number(range.from || 0)
  const to = range.to == null ? null : Number(range.to)
  if (to == null) return from > 0 ? `${formatINR(from)} & above` : "Any toy"
  return from > 0 ? `${formatINR(from)} – ${formatINR(to)}` : `Up to ${formatINR(to)}`
}

/** One "label: value" line. */
const Field = ({ label, children, md = "3" }) => (
  <Col xs="6" md={md} className="mb-3">
    <div className="text-muted font-size-12">{label}</div>
    <div className="fw-semibold">{children ?? "-"}</div>
  </Col>
)

/** Card section with a header bar (Plan / Customer / Payment). */
const Section = ({ icon, title, children, className = "" }) => (
  <div className={`border rounded-3 overflow-hidden ${className}`}>
    <div className="d-flex align-items-center gap-2 px-3 py-2 bg-light border-bottom">
      <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10 text-primary" style={{ width: 28, height: 28 }}>
        <i className={`mdi ${icon} font-size-16`} />
      </span>
      <h6 className="mb-0 fw-semibold">{title}</h6>
    </div>
    <div className="px-3 pt-3">{children}</div>
  </div>
)

function SubscriptionDetailsModal({ subscriptionId, toggle }) {
  const isOpen = !!subscriptionId
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [sub, setSub] = useState(null)
  const [orders, setOrders] = useState([])
  // "details" | "orders" — orders get their own tab so a long list doesn't stretch the popup.
  const [tab, setTab] = useState("details")

  const [viewId, setViewId] = useState(null)
  const [plans, setPlans] = useState([])

  useEffect(() => {
    if (!subscriptionId) {
      setViewId(null)
      return
    }
    let active = true
    setViewId(subscriptionId)
    setPlans([])
    Promise.all([
      get(`subscriptions/${subscriptionId}`),
      // extra — the popup still works without the picker if this fails
      get(`subscriptions/${subscriptionId}/history`).catch(() => []),
    ])
      .then(([current, others]) => {
        if (!active) return
        const list = [current, ...(Array.isArray(others) ? others : [])]
          .filter(Boolean)
          .sort((a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0))
        setPlans(
          list.map(p => ({
            _id: p._id,
            label: `${p.package?.name || "Plan"} · ${formatDate(p.startDate)} – ${formatDate(p.expiryDate)}${
              p._id === subscriptionId ? " (Current)" : ""
            }`,
          })),
        )
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [subscriptionId])

  useEffect(() => {
    if (!viewId) return
    let active = true
    setTab("details")
    setLoading(true)
    setError("")
    setSub(null)
    setOrders([])
    Promise.all([get(`subscriptions/${viewId}`), get(`subscriptions/${viewId}/orders`)])
      .then(([details, orderList]) => {
        if (!active) return
        setSub(details || null)
        setOrders(Array.isArray(orderList) ? orderList : [])
      })
      .catch(err => active && setError(errorMessage(err, "Could not load subscription details")))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [viewId])

  const customer = sub?.customer
  const pkg = sub?.package
  const pay = sub?.paymentDetails || {}
  const isManual = sub?.source === "admin"
  const isActive = sub?.expiryDate && moment(sub.expiryDate).isAfter(moment())
  const amountPaid = Number(sub?.amount || 0) - Number(sub?.discount || 0)
  const address = customer
    ? [customer.address?.addressLine1, customer.address?.addressLine2, customer.pincode].filter(Boolean).join(", ")
    : ""

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" scrollable>
      <ModalHeader toggle={toggle} tag="h4">
        Subscription Details
      </ModalHeader>
      <ModalBody>
        {plans.length > 1 && (
          <div className="d-flex align-items-center gap-2 mb-3">
            <label htmlFor="plan-picker" className="form-label mb-0 text-nowrap fw-semibold">
              Plan:
            </label>
            <select
              id="plan-picker"
              className="form-select"
              style={{ maxWidth: 420 }}
              value={viewId || ""}
              onChange={e => setViewId(e.target.value)}
            >
              {plans.map(p => (
                <option key={p._id} value={p._id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        )}
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : error ? (
          <p className="text-danger text-center py-4 mb-0">{error}</p>
        ) : sub ? (
          <>
            <Nav tabs className="nav-tabs-custom mb-3">
              <NavItem>
                <NavLink
                  href="#"
                  className={tab === "details" ? "active" : ""}
                  onClick={e => {
                    e.preventDefault()
                    setTab("details")
                  }}
                >
                  Subscription
                </NavLink>
              </NavItem>
              {/* Orders tab only when the subscription has orders */}
              {orders.length > 0 && (
                <NavItem>
                  <NavLink
                    href="#"
                    className={tab === "orders" ? "active" : ""}
                    onClick={e => {
                      e.preventDefault()
                      setTab("orders")
                    }}
                  >
                    Orders <span className="badge rounded-pill bg-primary ms-1">{orders.length}</span>
                  </NavLink>
                </NavItem>
              )}
            </Nav>

            {/* Same height for both tabs so the popup doesn't change size; content scrolls inside */}
            <TabContent activeTab={tab} style={{ height: "60vh" }}>
            <TabPane tabId="details" style={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>
            {/* Plan */}
            <Section icon="mdi-crown-outline" title="Plan" className="mb-4">
            <Row>
              <Field label="Package">{pkg?.name || "-"}</Field>
              <Field label="Status">
                <span className={`badge rounded-pill font-size-12 px-3 py-1 ${isActive ? "badge-soft-success" : "badge-soft-danger"}`}>
                  {isActive ? "Active" : "Expired"}
                </span>
              </Field>
              <Field label="Start Date">{formatDate(sub.startDate)}</Field>
              <Field label="End Date">{formatDate(sub.expiryDate)}</Field>
              <Field label="City">{sub.city?.name}</Field>
              <Field label="Turns">
                {sub.usedTurns} used / {sub.turns} total ({sub.turnsLeft} left)
              </Field>
              <Field label="Toy Range">{rangeText(pkg?.orderRange)}</Field>
              <Field label="Validity">{pkg?.validity ? `${pkg.validity} months` : "-"}</Field>
            </Row>
            </Section>

            {/* Customer + Payment side by side */}
            <Row className="g-4">
            <Col lg="6">
            <Section icon="mdi-account-outline" title="Customer" className="h-100">
            <Row>
              <Field md="4" label="Name">{[customer?.fname, customer?.lname].filter(Boolean).join(" ") || "-"}</Field>
              <Field md="4" label="Mobile">{formatMobile(customer?.mobile)}</Field>
              <Field md="4" label="Email">{customer?.email || "-"}</Field>
              {address && (
                <Col xs="12" className="mb-2">
                  <div className="text-muted font-size-12">Address</div>
                  <div className="fw-semibold">{address}</div>
                </Col>
              )}
            </Row>
            </Section>
            </Col>

            <Col lg="6">
            <Section icon="mdi-credit-card-outline" title="Payment" className="h-100">
            <Row>
              <Field md="4" label="Plan Price">{formatINR(sub.amount)}</Field>
              <Field md="4" label="Discount">
                {Number(sub.discount || 0) > 0 ? `− ${formatINR(sub.discount)}` : "-"}
                {sub.promoCode?.code && <span className="text-muted"> ({sub.promoCode.code})</span>}
              </Field>
              <Field md="4" label="Amount Paid">{formatINR(amountPaid)}</Field>
              <Field md="4" label="Added By">{isManual ? "Admin" : "Online"}</Field>
              {isManual ? (
                <>
                  <Field md="4" label="Payment Mode">{PAYMENT_MODE_LABEL[pay.method] || pay.method || "-"}</Field>
                  <Field md="4" label="Reference No.">{pay.referenceNo || "-"}</Field>
                  {sub.note && (
                    <Col xs="12" className="mb-2">
                      <div className="text-muted font-size-12">Note</div>
                      <div className="fw-semibold">{sub.note}</div>
                    </Col>
                  )}
                </>
              ) : (
                <>
                  <Field md="4" label="Payment Method">{pay.method ? String(pay.method).toUpperCase() : "Online"}</Field>
                </>
              )}
              <Field md="4" label="Purchased On">{formatDate(sub.createdAt)}</Field>
            </Row>
            </Section>
            </Col>
            </Row>
            </TabPane>

            {/* Orders — own scroll area, header stays visible */}
            <TabPane tabId="orders" style={{ height: "100%" }}>
            {orders.length === 0 ? (
              <p className="text-muted mb-0">No orders booked with this subscription yet.</p>
            ) : (
              <div className="table-responsive" style={{ height: "100%", overflowY: "auto" }}>
                <Table className="table align-middle table-nowrap mb-0">
                  <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <tr>
                      <th>Order ID</th>
                      <th>Toy</th>
                      <th>Delivery</th>
                      <th>Return</th>
                      <th>Weeks</th>
                      <th>Turns</th>
                      <th>Deposit</th>
                      <th>Delivery Fee</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(o => (
                      <tr key={o._id}>
                        <td>#{o.orderId}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {o.toy?.defaultPhoto && (
                              <img
                                src={o.toy.defaultPhoto}
                                alt=""
                                style={{ width: 36, height: 36, objectFit: "contain" }}
                                className="rounded border"
                              />
                            )}
                            <div>
                              <div className="fw-semibold">{o.toy?.name || "-"}</div>
                              {o.toy?.code && <div className="text-muted font-size-12">{o.toy.code}</div>}
                            </div>
                          </div>
                        </td>
                        <td>{formatDate(o.deliveryDate)}</td>
                        <td>{formatDate(o.returnDate)}</td>
                        <td>{o.duration}</td>
                        <td className={["cancelled", "refunded"].includes(o.status) ? "text-decoration-line-through text-muted" : ""}>
                          {o.turns}
                        </td>
                        <td>{formatINR(o.deposit)}</td>
                        <td>{o.selfPickup ? "Self pickup" : formatINR(o.deliveryFee)}</td>
                        <td>
                          <span
                            className={`badge rounded-pill font-size-12 px-3 py-1 text-capitalize ${
                              ORDER_STATUS_BADGE[o.status] || "badge-soft-secondary"
                            }`}
                          >
                            {o.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
            </TabPane>
            </TabContent>
          </>
        ) : null}
      </ModalBody>
    </Modal>
  )
}

export default SubscriptionDetailsModal
